/**
 * SecurityCamera - Surveillance camera entity
 * Allows player to view different areas of the level
 */

import * as THREE from 'three';
import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';

export interface SecurityCameraConfig {
  id: string;
  position: Vector2;
  size: { width: number; height: number };
  viewAngle: number; // degrees
  viewDistance: number; // pixels
  rotationSpeed: number; // degrees per second
  panRange?: { min: number; max: number }; // rotation limits
}

export class SecurityCamera extends Entity {
  // Camera properties
  public cameraId: string;
  public viewAngle: number;
  public viewDistance: number;
  public rotationSpeed: number;
  public panRange: { min: number; max: number };

  // State
  private currentRotation: number = 0; // degrees
  private targetRotation: number = 0; // degrees
  private isActive: boolean = true;
  private isPanning: boolean = false;
  private panDirection: number = 1; // 1 = right, -1 = left

  // Viewing
  private isBeingViewed: boolean = false;
  private viewCooldown: number = 0;
  private maxViewTime: number = 30; // seconds before needing cooldown

  // Visual rendering
  private mesh: THREE.Group | null = null;
  private bodyMesh: THREE.Mesh | null = null;
  private lensMesh: THREE.Mesh | null = null;

  constructor(config: SecurityCameraConfig) {
    super(config.id, 'security_camera', config.position, config.size);

    this.cameraId = config.id;
    this.viewAngle = config.viewAngle;
    this.viewDistance = config.viewDistance;
    this.rotationSpeed = config.rotationSpeed;
    this.panRange = config.panRange || { min: -90, max: 90 };

    // Cameras don't have health
    this.health = -1;
    this.maxHealth = -1;

    // Start at center of pan range
    this.currentRotation = (this.panRange.min + this.panRange.max) / 2;
    this.targetRotation = this.currentRotation;

    // Create visual mesh
    this.createMesh();
  }

  /**
   * Create Three.js mesh for the camera
   */
  private createMesh(): void {
    this.mesh = new THREE.Group();

    // Camera body (rectangular box)
    const bodyGeometry = new THREE.BoxGeometry(25, 15, 20);
    const bodyMaterial = new THREE.MeshBasicMaterial({ color: 0x2a2a2a });
    this.bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.mesh.add(this.bodyMesh);

    // Camera lens (cylinder)
    const lensGeometry = new THREE.CylinderGeometry(5, 5, 8, 16);
    const lensMaterial = new THREE.MeshBasicMaterial({ color: 0x1a1a1a });
    this.lensMesh = new THREE.Mesh(lensGeometry, lensMaterial);
    this.lensMesh.rotation.z = Math.PI / 2; // Rotate to point forward
    this.lensMesh.position.set(12, 0, 0);
    this.mesh.add(this.lensMesh);

    // Lens glass (glowing when active)
    const glassGeometry = new THREE.CircleGeometry(4, 16);
    const glassColor = this.isActive ? 0xff0000 : 0x444444;
    const glassMaterial = new THREE.MeshBasicMaterial({
      color: glassColor,
      opacity: 0.8,
      transparent: true
    });
    const glassMesh = new THREE.Mesh(glassGeometry, glassMaterial);
    glassMesh.position.set(16, 0, 0);
    this.mesh.add(glassMesh);

    // Camera mount (connects to wall/ceiling)
    const mountGeometry = new THREE.BoxGeometry(8, 8, 8);
    const mountMaterial = new THREE.MeshBasicMaterial({ color: 0x404040 });
    const mountMesh = new THREE.Mesh(mountGeometry, mountMaterial);
    mountMesh.position.set(-12, 0, 0);
    this.mesh.add(mountMesh);

    // Position and rotate the mesh
    this.mesh.position.set(this.position.x, this.position.y, 0);
    this.mesh.rotation.z = (this.currentRotation * Math.PI) / 180;
  }

  /**
   * Update camera state
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active || !this.isActive) return;

    // Update rotation
    this.updateRotation(deltaTime);

    // Update panning
    if (this.isPanning) {
      this.updatePanning(deltaTime);
    }

    // Update view cooldown
    if (this.viewCooldown > 0) {
      this.viewCooldown = Math.max(0, this.viewCooldown - deltaTime);
    }

    // Update mesh position and rotation
    if (this.mesh) {
      this.mesh.position.set(this.position.x, this.position.y, 0);
      this.mesh.rotation.z = (this.currentRotation * Math.PI) / 180;

      // Update lens color based on state
      const glassMesh = this.mesh.children[2]; // Glass is the 3rd child
      if (glassMesh instanceof THREE.Mesh) {
        const material = glassMesh.material as THREE.MeshBasicMaterial;
        if (this.isBeingViewed) {
          material.color.setHex(0x00ff00); // Green when being viewed
        } else if (this.isActive) {
          material.color.setHex(0xff0000); // Red when active
        } else {
          material.color.setHex(0x444444); // Gray when inactive
        }
      }
    }
  }

  /**
   * Render camera (placeholder for Three.js rendering)
   */
  public render(): void {
    // Will be implemented with Three.js sprite rendering
  }

  /**
   * Update camera rotation
   */
  private updateRotation(deltaTime: number): void {
    if (Math.abs(this.currentRotation - this.targetRotation) < 0.1) {
      this.currentRotation = this.targetRotation;
      return;
    }

    const rotationDelta = this.rotationSpeed * deltaTime;
    const diff = this.targetRotation - this.currentRotation;

    if (Math.abs(diff) <= rotationDelta) {
      this.currentRotation = this.targetRotation;
    } else {
      this.currentRotation += Math.sign(diff) * rotationDelta;
    }

    // Clamp to pan range
    this.currentRotation = Math.max(
      this.panRange.min,
      Math.min(this.panRange.max, this.currentRotation)
    );
  }

  /**
   * Update automatic panning
   */
  private updatePanning(deltaTime: number): void {
    this.targetRotation += this.panDirection * this.rotationSpeed * deltaTime;

    // Reverse direction at limits
    if (this.targetRotation >= this.panRange.max) {
      this.targetRotation = this.panRange.max;
      this.panDirection = -1;
    } else if (this.targetRotation <= this.panRange.min) {
      this.targetRotation = this.panRange.min;
      this.panDirection = 1;
    }
  }

  /**
   * Start viewing this camera
   */
  public startViewing(): boolean {
    if (!this.isActive) {
      console.log(`Camera '${this.cameraId}' is not active`);
      return false;
    }

    if (this.viewCooldown > 0) {
      console.log(`Camera '${this.cameraId}' is on cooldown`);
      return false;
    }

    this.isBeingViewed = true;
    this.isPanning = false; // Stop auto-panning when viewed
    console.log(`Viewing camera '${this.cameraId}'`);

    return true;
  }

  /**
   * Stop viewing this camera
   */
  public stopViewing(): void {
    if (!this.isBeingViewed) {
      return;
    }

    this.isBeingViewed = false;
    this.isPanning = true; // Resume auto-panning
    console.log(`Stopped viewing camera '${this.cameraId}'`);
  }

  /**
   * Rotate camera to specific angle
   */
  public rotateTo(angle: number): void {
    this.targetRotation = Math.max(
      this.panRange.min,
      Math.min(this.panRange.max, angle)
    );
  }

  /**
   * Pan camera left
   */
  public panLeft(deltaTime: number): void {
    if (!this.isBeingViewed) return;

    this.targetRotation -= this.rotationSpeed * deltaTime * 2; // 2x speed for manual control
    this.targetRotation = Math.max(this.panRange.min, this.targetRotation);
  }

  /**
   * Pan camera right
   */
  public panRight(deltaTime: number): void {
    if (!this.isBeingViewed) return;

    this.targetRotation += this.rotationSpeed * deltaTime * 2; // 2x speed for manual control
    this.targetRotation = Math.min(this.panRange.max, this.targetRotation);
  }

  /**
   * Enable automatic panning
   */
  public enableAutoPanning(): void {
    this.isPanning = true;
  }

  /**
   * Disable automatic panning
   */
  public disableAutoPanning(): void {
    this.isPanning = false;
  }

  /**
   * Disable camera (power outage, etc.)
   */
  public disable(): void {
    this.isActive = false;
    this.stopViewing();
    console.log(`Camera '${this.cameraId}' disabled`);
  }

  /**
   * Enable camera
   */
  public enable(): void {
    this.isActive = true;
    this.isPanning = true;
    console.log(`Camera '${this.cameraId}' enabled`);
  }

  /**
   * Check if position is in camera view
   */
  public isPositionInView(position: Vector2): boolean {
    if (!this.isActive) {
      return false;
    }

    // Calculate distance to position
    const dx = position.x - this.position.x;
    const dy = position.y - this.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > this.viewDistance) {
      return false;
    }

    // Calculate angle to position
    const angleToTarget = (Math.atan2(dy, dx) * 180) / Math.PI;
    const angleDiff = Math.abs(angleToTarget - this.currentRotation);

    // Check if within view angle
    return angleDiff <= this.viewAngle / 2;
  }

  // Getters
  public getIsActive(): boolean {
    return this.isActive;
  }

  public getIsBeingViewed(): boolean {
    return this.isBeingViewed;
  }

  public getCurrentRotation(): number {
    return this.currentRotation;
  }

  public getTargetRotation(): number {
    return this.targetRotation;
  }

  public getIsPanning(): boolean {
    return this.isPanning;
  }

  public getViewCooldown(): number {
    return this.viewCooldown;
  }

  public getMesh(): THREE.Group | null {
    return this.mesh;
  }

  /**
   * Get camera info for UI/debugging
   */
  public getInfo(): {
    id: string;
    active: boolean;
    beingViewed: boolean;
    rotation: number;
    panning: boolean;
    viewAngle: number;
    viewDistance: number;
  } {
    return {
      id: this.cameraId,
      active: this.isActive,
      beingViewed: this.isBeingViewed,
      rotation: this.currentRotation,
      panning: this.isPanning,
      viewAngle: this.viewAngle,
      viewDistance: this.viewDistance,
    };
  }
}
